// Last-active primary-mode persistence.
//
// On reload we want to drop the user back into whichever workspace mode
// they had open last (per dev-sync note f4ffe4de), with a defensive
// fallback when the persisted mode is no longer accessible -- e.g. a
// role change between sessions, or a stale value from a prior build
// where the mode id has since been retired/renamed.
//
// The fallback is "the home (default) mode of the user's first
// accessible bucket" -- i.e. `getDefaultModeForBucket` of the first
// entry in `getAccessibleBuckets(auth)`. That's the same primary
// surface a brand-new session of this user would land on if no value
// were persisted at all, so we don't introduce a separate notion of
// "primary bucket" to maintain.
import type { AuthContextValue } from "@/contexts/AuthContext";
import {
  canAccessPrimaryMode,
  type PrimaryMode,
} from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import {
  MODE_TO_BUCKET,
  getAccessibleBuckets,
  getDefaultModeForBucket,
} from "@/components/shell/modes/bucketsRegistry";
import type { AnchorEntitlements } from "@/lib/entitlements/anchorsClient";
import { canAccessByAnchor } from "@/lib/entitlements/modeAnchorRequirement";
import {
  MFG_DESK_ROLES,
  MFG_PURCHASING_ROLE,
} from "@/lib/roles/turboManufacturingRoles";

const STORAGE_KEY = "bb-last-primary-mode";

/**
 * Renamed-mode migration table. Keys are persisted ids that no longer
 * exist in `PrimaryMode`; values are the current canonical id they map
 * to. Read-path uses this to translate stale `localStorage` values so a
 * returning user lands on the equivalent surface instead of falling
 * through to the bucket-home fallback.
 *
 *  - `mfgOrderIntake` (2026-06-15): moved from the Manufacturing
 *    bucket to the Sales bucket as `salesIntake`, role-scoped into
 *    three variants. Mfg-role users who had this open keep landing on
 *    the same Mfg-accepter content; the wrapper picks the variant by
 *    role tokens.
 *  - `proposalFormats` (2026-06-17): renamed to `formsAndFormats`
 *    with a Proposals / Forms primary selector; proposal editor
 *    content is unchanged under the Proposals sub-mode.
 */
const LEGACY_MODE_MIGRATIONS: Record<string, PrimaryMode> = {
  mfgOrderIntake: "salesIntake",
  // 2026-06-17 (F6): Manager mode renamed to host Proposals + Forms.
  proposalFormats: "formsAndFormats",
  // 2026-06-26 (S3): Offerings authoring moved to Forms & formats → Service Quotes.
  serviceOfferings: "formsAndFormats",
  // 2026-06-26: Rigs + Techs merged into Resources, then Resources → Service setup.
  serviceRigs: "serviceDefinitions",
  serviceTechs: "serviceDefinitions",
  serviceResources: "serviceDefinitions",
  // 2026-06-29: Calendar + Map merged into Scheduling.
  serviceCalendar: "serviceScheduling",
  serviceMap: "serviceScheduling",
  // 2026-06-30: Job types + cost rates were on Board; job types moved to Service setup (Slice 3).
  serviceWorkQueues: "serviceBoard",
  serviceLaborRates: "serviceBoard",
  // 2026-07-06: Retired standalone Document Builder modes; content lives
  // under Documents Editor → Documents sub-mode.
  documentLibrary: "formsAndFormats",
  documentEditor: "formsAndFormats",
  // 2026-07-28: Time Study + BOM Audit merged into Production → Audits.
  mfgTimeStudy: "mfgAudits",
  mfgBomAudit: "mfgAudits",
  // 2026-08-07: Units roster folded under Build pipeline → Units sub-tab.
  mfgUnits: "mfgBuildPipeline",
  // 2026-09-09: Vendor directory folded into Production → Purchasing.
  mfgVendors: "mfgPurchasing",
  // 2026-10-23: Ops Labor pay folded into Production → Labor pay. Writes follow role.
  opsLaborPay: "mfgLaborPay",
};

/** Read the persisted last mode; returns null if missing, malformed, or
 *  not a currently-known PrimaryMode (e.g. the id was retired since the
 *  value was written). Persisted ids that map through
 *  `LEGACY_MODE_MIGRATIONS` are translated to their canonical
 *  successor so a returning user lands on the equivalent surface. */
export function readLastPrimaryMode(): PrimaryMode | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (!v) return null;
    if (Object.prototype.hasOwnProperty.call(MODE_TO_BUCKET, v)) {
      return v as PrimaryMode;
    }
    if (Object.prototype.hasOwnProperty.call(LEGACY_MODE_MIGRATIONS, v)) {
      return LEGACY_MODE_MIGRATIONS[v];
    }
    return null;
  } catch {
    return null;
  }
}

export function writeLastPrimaryMode(mode: PrimaryMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // ignore (private mode, quota, SSR)
  }
}

/** First accessible bucket's home mode, or null if the user has zero
 *  accessible modes (defensive -- shouldn't happen in normal sessions).
 *
 *  Anchor entitlements compose with the role gate: if loaded, modes whose
 *  anchor requirement isn't satisfied are filtered out. Passing `undefined`
 *  (or omitting) keeps the pre-2026-06-15 role-only behavior — matches the
 *  loading semantics of `canAccessByAnchor`. */
export function resolveRoleBucketHome(
  auth: AuthContextValue,
  anchors?: AnchorEntitlements | null
): PrimaryMode | null {
  // Purchasing desk home is Manufacturing → Inventory even when Production
  // count is also visible (registry order would otherwise land on count).
  const purchasingDesk =
    auth.hasRole(MFG_PURCHASING_ROLE) && !auth.hasRole(...MFG_DESK_ROLES);
  if (
    purchasingDesk &&
    canAccessPrimaryMode("mfgInventory", auth) &&
    canAccessByAnchor("mfgInventory", anchors ?? null)
  ) {
    return "mfgInventory";
  }

  const buckets = getAccessibleBuckets(auth, anchors);
  for (const bucket of buckets) {
    const home = getDefaultModeForBucket(bucket.id, auth, anchors);
    if (home) return home;
  }
  return null;
}

/** Pick the mode to land on for an authenticated session: the candidate
 *  if still accessible, otherwise the role-bucket home. */
export function resolveInitialMode(
  candidate: PrimaryMode | null,
  auth: AuthContextValue,
  anchors?: AnchorEntitlements | null
): PrimaryMode | null {
  if (
    candidate &&
    canAccessPrimaryMode(candidate, auth) &&
    canAccessByAnchor(candidate, anchors ?? null)
  ) {
    return candidate;
  }
  return resolveRoleBucketHome(auth, anchors);
}
